import React, { useState } from 'react';
import { Box, Table, Thead, Tbody, Tr, Th, Td, Button, IconButton, Flex, Modal, ModalOverlay, ModalContent, ModalHeader, ModalFooter, ModalBody, ModalCloseButton, useDisclosure, FormControl, FormLabel, Input, Grid, Select, Avatar, Badge } from '@chakra-ui/react';
import { EditIcon, DeleteIcon, AddIcon, ViewIcon } from '@chakra-ui/icons';
import { getTechnicians, createTechnician, updateTechnician, deleteTechnician } from '../../Utilities/networks';
import { useNavigate } from 'react-router-dom';

const Technicians = () => {
  const [techList, setTechList] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterArea, setFilterArea] = useState('');

  const { isOpen, onOpen, onClose } = useDisclosure();
  const { isOpen: isOpenDelete, onOpen: onOpenDelete, onClose: onCloseDelete } = useDisclosure();
  const [deletingTech, setDeletingTech] = useState(null);
  const [editingTech, setEditingTech] = useState(null);

  const navigate = useNavigate();

  // Form fields
  const [newName, setNewName] = useState('');
  const [newPhone, setNewPhone] = useState('');
  const [newEmail, setNewEmail] = useState('');
  const [newArea, setNewArea] = useState('');
  const [newStatus, setNewStatus] = useState('Active');
  const [newWorkingHours, setNewWorkingHours] = useState('');
  const [newProfileImage, setNewProfileImage] = useState('');
  const [newTechnicianStatus, setNewTechnicianStatus] = useState('');

  // Fetch technicians from backend
  React.useEffect(() => {
    fetchTechnicians();
  }, []);

  const fetchTechnicians = async () => {
    try {
      const res = await getTechnicians();
      if (res.data && res.data.data) {
        setTechList(res.data.data);
      }
    } catch (err) {
      // handle error (optional)
    }
  };

  const handleAdd = () => {
    setEditingTech(null);
    setNewName('');
    setNewPhone('');
    setNewEmail('');
    setNewArea('');
    setNewStatus('Active');
    setNewWorkingHours('');
    setNewProfileImage('');
    setNewTechnicianStatus('');
    onOpen();
  };

  const handleEdit = (tech) => {
    setEditingTech(tech);
    setNewName(tech.name);
    setNewPhone(tech.phone);
    setNewEmail(tech.email || '');
    setNewArea(tech.area);
    setNewStatus(tech.status);
    setNewWorkingHours(tech.working_hours || tech.workingHours);
    setNewProfileImage(tech.profile_image || tech.profileImage);
    setNewTechnicianStatus(tech.technician_status || tech.technicianStatus || '');
    onOpen();
  };

  const handleSave = async () => {
    const payload = {
      name: newName,
      phone: newPhone,
      email: newEmail,
      area: newArea,
      status: newStatus,
      working_hours: newWorkingHours,
      profile_image: newProfileImage,
      technician_status: newTechnicianStatus,
    };
    try {
      if (editingTech) {
        await updateTechnician({ ...payload, id: editingTech.id });
      } else {
        await createTechnician(payload);
      }
      fetchTechnicians();
      setEditingTech(null);
      onClose();
    } catch (err) {
      // handle error (optional)
    }
  };

  const handleDeleteClick = (tech) => {
    setDeletingTech(tech);
    onOpenDelete();
  };

  const confirmDelete = async () => {
    try {
      await deleteTechnician(deletingTech.id);
      fetchTechnicians();
    } catch (err) {
      // handle error (optional)
    }
    setDeletingTech(null);
    onCloseDelete();
  };

  // Filter technicians by search term and area/zone; case-insensitive matching on name/phone/area.
  const filteredTech = techList.filter(tech => {
    const searchMatch =
      tech.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      tech.phone.toLowerCase().includes(searchTerm.toLowerCase()) ||
      (tech.email && tech.email.toLowerCase().includes(searchTerm.toLowerCase())) ||
      tech.area.toLowerCase().includes(searchTerm.toLowerCase());
    const areaMatch = filterArea ? tech.area === filterArea : true;
    return searchMatch && areaMatch;
  });

  // Get unique areas for filter dropdown
  const uniqueAreas = [...new Set(techList.map(t => t.area))];

  return (
    <Box p={4}>
      {/* Header */}
      <Flex align="center" justify="space-between" mb={4}>
        <Box fontSize="xl" fontWeight="bold">Technicians List</Box>
        <Button leftIcon={<AddIcon />} colorScheme="teal" onClick={handleAdd}>
          Add Technician
        </Button>
      </Flex>

      {/* Search and Filter */}
      <Flex mb={4} gap={4}>
        <FormControl maxW="300px">
          <FormLabel mb={1}>Search</FormLabel>
          <Input 
            placeholder="Search by name, phone, or area" 
            value={searchTerm} 
            onChange={(e) => setSearchTerm(e.target.value)} 
          />
        </FormControl>
        <FormControl maxW="200px">
          <FormLabel mb={1}>Filter by Area/Zone</FormLabel>
          <Select 
            placeholder="All"
            value={filterArea} 
            onChange={(e) => setFilterArea(e.target.value)}
          >
            {uniqueAreas.map(area => (
              <option key={area} value={area}>{area}</option>
            ))}
          </Select>
        </FormControl>
      </Flex>

      {/* Technicians Table */}
      <Table variant="outline" sx={{ td: { border: '1px solid', borderColor: 'gray.200' }, th: { border: '1px solid', borderColor: 'gray.200' } }}>
        <Thead>
          <Tr>
            <Th>Profile</Th>
            <Th>ID</Th>
            <Th>Technician Status</Th>
            <Th>Name</Th>
            <Th>Phone (ID)</Th>
            <Th>Email</Th>
            <Th>Area/Zone</Th>
            <Th>Working Hours</Th>
            <Th>Status</Th>
            <Th>Actions</Th>
          </Tr>
        </Thead>
        <Tbody>
          {filteredTech.map(tech => (
            <Tr key={tech.id}>
              <Td>
                {tech.profileImage ? (
                  <Avatar size="sm" src={tech.profileImage} name={tech.name} />
                ) : (
                  <Avatar size="sm" name={tech.name} />
                )}
              </Td>
              <Td>{tech.id}</Td>
              <Td>
                <Badge
                  colorScheme={
                    tech.technician_status?.toLowerCase() === 'online' ? 'green' :
                    tech.technician_status?.toLowerCase() === 'offline' ? 'red' :
                    'gray'
                  }
                >
                  {tech.technician_status || tech.technicianStatus || '-'}
                </Badge>
              </Td>
              <Td>{tech.name}</Td>
              <Td>{tech.phone}</Td>
              <Td>{tech.email || '-'}</Td>
              <Td>{tech.area}</Td>
              <Td>{tech.working_hours}</Td>
              <Td>
                <Badge colorScheme={tech.status === 'Active' ? 'green' : 'red'}>
                  {tech.status}
                </Badge>
              </Td>
              <Td>
                <IconButton aria-label="View Technician" icon={<ViewIcon />} size="sm" mr={2} onClick={() => navigate(`/technicians/${tech.id}`, { state: { technician: tech } })} />
                <IconButton aria-label="Edit Technician" icon={<EditIcon />} size="sm" mr={2} onClick={() => handleEdit(tech)} />
                <IconButton aria-label="Delete Technician" icon={<DeleteIcon />} size="sm" onClick={() => handleDeleteClick(tech)} />
              </Td>
            </Tr>
          ))}
        </Tbody>
      </Table>

      {/* Add/Edit Technician Modal */}
      <Modal isOpen={isOpen} onClose={onClose}>
        <ModalOverlay />
        <ModalContent size="5xl">
          <ModalHeader textAlign="center">{editingTech ? "Edit Technician" : "Add Technician"}</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <Grid templateColumns="repeat(2, 1fr)" gap={6}>
              <FormControl>
                <FormLabel>Name</FormLabel>
                <Input value={newName} onChange={(e) => setNewName(e.target.value)} placeholder="Enter name" />
              </FormControl>
              <FormControl>
                <FormLabel>Phone Number (ID)</FormLabel>
                <Input value={newPhone} onChange={(e) => setNewPhone(e.target.value)} placeholder="Enter phone number" />
              </FormControl>
              <FormControl mt={6}>
                <FormLabel>Email</FormLabel>
                <Input value={newEmail} onChange={(e) => setNewEmail(e.target.value)} placeholder="Enter email (optional)" />
              </FormControl>
              <FormControl mt={6}>
                <FormLabel>Area/Zone</FormLabel>
                <Input value={newArea} onChange={(e) => setNewArea(e.target.value)} placeholder="Enter area/zone" />
              </FormControl>
              <FormControl mt={6}>
                <FormLabel>Working Hours</FormLabel>
                <Input value={newWorkingHours} onChange={(e) => setNewWorkingHours(e.target.value)} placeholder="e.g. 9am-5pm" />
              </FormControl>
              <FormControl mt={6}>
                <FormLabel>Profile Image URL</FormLabel>
                <Input value={newProfileImage} onChange={(e) => setNewProfileImage(e.target.value)} placeholder="Paste image URL" />
              </FormControl>
              <FormControl mt={6}>
                <FormLabel>Status</FormLabel>
                <Select value={newStatus} onChange={(e) => setNewStatus(e.target.value)}>
                  <option value="Active">Active</option>
                  <option value="Inactive">Inactive</option>
                </Select>
              </FormControl>
              <FormControl mt={6}>
                <FormLabel>Technician Status</FormLabel>
                <Input value={newTechnicianStatus} onChange={(e) => setNewTechnicianStatus(e.target.value)} placeholder="e.g. On Duty / Off Duty" />
              </FormControl>
            </Grid>
          </ModalBody>
          <ModalFooter justifyContent="center">
            <Button colorScheme="teal" mx={3} onClick={handleSave}>
              Save
            </Button>
            <Button variant="ghost" mx={3} onClick={onClose}>
              Cancel
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      {/* Delete Confirmation Modal */}
      <Modal isOpen={isOpenDelete} onClose={() => { onCloseDelete(); setDeletingTech(null); }}>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader textAlign="center">Confirm Delete</ModalHeader>
          <ModalCloseButton />
          <ModalBody textAlign="center">
            {deletingTech && (
              <Box>
                Are you sure you want to delete <strong>{deletingTech.name}</strong>?
              </Box>
            )}
          </ModalBody>
          <ModalFooter justifyContent="center">
            <Button colorScheme="red" mx={3} onClick={confirmDelete}>
              Delete
            </Button>
            <Button variant="ghost" mx={3} onClick={() => { onCloseDelete(); setDeletingTech(null); }}>
              Cancel
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </Box>
  );
};

export default Technicians;
